import { Button } from '@/components/ui/button';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { currencyInputValueToCents } from '@/lib/currency';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { paymentStore } from '@/models/stores';
import type { Payment } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Plus } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { v4 as uuidv4 } from 'uuid';
import CreatePaymentDialog from './create-payment-dialog';

export const FORMAT_DATETIME_STRING = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const FORMAT_DATETIME_FRIENDLY_STRING = 'YYYY-MM-DD HH:mm:ss Z';
export const FORMAT_DATE_STRING = 'YYYY-MM-DD';

const AddPayment = ({
  dealId,
}: // shortId,
{
  dealId: Deal['id'];
  // shortId: Deal["short_id"];
}) => {
  const user = useUser();
  // const queryClient = useQueryClient();

  const createPaymentMutation = useMutation({
    onMutate: async ({ payment }: { payment: Payment }) =>
      // { payment }: { payment: Payment }
      {
        // DEV NOTE: for now we're not going to have optimistic payments
        // because it requires handle the race condition where the broadcast
        // updates first or if the HTTP request finishes first and we'll have to
        // link the optimistic payment to the real payment
        // TODO: implement
        paymentStore.add({
          ...payment,
        });
      },
    mutationFn: async ({ payment }: { payment: Payment }) => {
      // /api/deals/:id/payments/create
      const response = await pdkAxios
        .post(`/deals/${dealId}/payments/create`, payment)
        .catch((e) => {
          console.error('Error creating payment', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error creating payment', error, variables);
    },
    onSettled: () => {
      // queryClient.invalidateQueries({ queryKey: ["deal", shortId] });
    },
  });

  const handleCreatePayment = useCallback(
    (newPayment: Payment) => {
      createPaymentMutation.mutate({
        payment: {
          ...newPayment,
        },
      });
    },
    [createPaymentMutation],
  );

  const buttonRef = useRef<HTMLButtonElement>(null);

  useHotkeys(HOTKEYS.DEAL_ADD_PAYMENT, (e) => {
    e.preventDefault();
    e.stopPropagation();

    // TODO (jimmy): don't know why but this opens two dialogs
    buttonRef.current?.click();
  });

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const buttonClassName = '';

  return (
    <div className="flex flex-row gap-2 items-center flex-wrap justify-between md:justify-normal">
      <CreatePaymentDialog
        key={resetKey}
        initialAmount={'0.00'}
        initialIntendedPaymentDate={dayjs().format(FORMAT_DATE_STRING)}
        initialMethod="cash"
        initialType="down_payment"
        initialIsFullPayment={false}
        ctaText="Add Payment"
        onOpen={() => {
          setDialogIsOpen(true);
        }}
        onClose={() => {
          setDialogIsOpen(false);
        }}
        dialogIsOpen={dialogIsOpen}
        onSubmit={({
          amount,
          intendedPaymentDate,
          method,
          type,
          isFullPayment,
        }) => {
          const amountCents = currencyInputValueToCents(amount);

          if (amountCents === 0 || amountCents < 0) {
            toast({
              title: 'Amount is invalid',
              description: 'Please enter a positive amount',
            });
            return;
          }

          if (!intendedPaymentDate) {
            toast({
              title: 'Intended Payment Date is required',
              description: 'Please enter an intended payment date',
            });
            return;
          }

          handleCreatePayment({
            id: uuidv4(),
            deal_id: dealId,
            amount: amountCents,
            intent_date: dayjs(intendedPaymentDate).format(FORMAT_DATE_STRING),
            method,
            type,
            is_full_payment: isFullPayment,
            created_by_id: user.id,
            sent_date: null,
            received_date: null,
            confirmed_date: null,
            refunded_date: null,
            receipt_file_url: null,
            receipt_file_name: null,
            receipt_file_type: null,
            receipt_file_size: null,
            receipt_uploaded_by_id: null,
            deleted_at: null,
            created_at: dayjs().toISOString(),
            updated_at: null,
          });

          setDialogIsOpen(false);

          // UX NOTE: This is a hack to force the dialog to reset its state
          setResetKey(resetKey + 1);
        }}
      >
        <Button
          variant="ghost"
          className={cn('p-1 h-fit', buttonClassName)}
          ref={buttonRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDialogIsOpen(true);
          }}
        >
          <Plus className={cn('h-3.5 w-3.5 cursor-pointer', {})} />
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="rounded text-xs  text-gray-600">Add Payment</p>
            </TooltipTrigger>
            <TooltipContent className="flex flex-row gap-2 items-center">
              Add Payment
              <KeyboardKeyIcon>{HOTKEYS.DEAL_ADD_PAYMENT}</KeyboardKeyIcon>
            </TooltipContent>
          </Tooltip>
        </Button>
      </CreatePaymentDialog>
    </div>
  );
};

export default AddPayment;
